import React from "react";
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { ElectricalPanelTypes, MaintenanceState } from "api/automationTypes";
import { BlockStandard } from "components/admin/BlockStandard";
import { DeleteCTA } from "components/MUIExtensions/DeleteCTA";
import { PhotoUploadGrid } from "components/forms/PhotoUploadGrid";

const TENSION_OPTIONS = [
  "127 V",
  "220 V",
  "127 V / 220 V",
  "220 V / 380 V",
  "Outro",
];

const YES_NO_OPTIONS = [
  { label: "Sim", value: true },
  { label: "Não", value: false },
];

const NR10_ITEMS: Array<{ key: keyof Nr10Checklist; label: string }> = [
  { key: "panelIdentification", label: "1. Identificação do quadro elétrico" },
  {
    key: "devicesIdentification",
    label: "2. Identificação das cargas nos dispositivos de proteção e manobra",
  },
  {
    key: "accidentalContactProtection",
    label:
      "3. Proteção contra contato acidental com partes vivas expostas (placa de acrílico)",
  },
  {
    key: "surgeProtectionDevice",
    label: "4. Dispositivo de proteção contra surtos (DPS)",
  },
  {
    key: "groundingAndEquipotential",
    label: "5. Aterramento de partes metálicas e equipotencializações",
  },
  {
    key: "neutralEarthSeparation",
    label: "6. Barramentos e separação de condutores de neutro e terra",
  },
  {
    key: "cableSectionAdequacy",
    label:
      "7. Seção do cabo adequada à carga e ao dispositivo de proteção utilizado",
  },
  {
    key: "workLockout",
    label: "8. Aplicação de bloqueios durante a realização de trabalhos",
  },
  {
    key: "warningPlates",
    label:
      "9. Placas de advertência (risco elétrico, nível de tensão e uso de EPIs)",
  },
  {
    key: "electricalDiagrams",
    label:
      "10. Diagramas elétricos com identificação do ponto de alimentação do quadro elétrico",
  },
  {
    key: "cableEntrySealing",
    label: "11. Vedação da entrada e saída de cabos (espuma expansiva)",
  },
  {
    key: "cableOrganization",
    label: "12. Organização dos cabos dentro do quadro elétrico",
  },
  {
    key: "internalSpaceAdequacy",
    label:
      "13. Espaço interno suficiente para comportar todos os dispositivos de forma adequada",
  },
];

const CONSERVATION_STATES = Object.values(MaintenanceState);

export const ElectricalPanelDetail = ({
  data,
  position,
  panelOptions,
  onChange,
  onDelete,
}: ElectricalPanelDetailProps) => {
  const handleFieldChange =
    (field: keyof ElectricalPanelDetailItem) =>
    ({ target }) => {
      onChange({ ...data, [field]: target.value });
    };

  const handlePanelTypeChange = ({ target }) => {
    onChange({ ...data, panelTypeId: target.value });
  };

  const handleNr10Change =
    (key: keyof Nr10Checklist) =>
    ({ target }) => {
      onChange({
        ...data,
        nr10: {
          ...data.nr10,
          [key]: target.value === "true",
        },
      });
    };

  const handlePhotosChange = (photos: File[]) => {
    onChange({ ...data, photos });
  };

  return (
    <BlockStandard>
      <TextField
        fullWidth
        label="Quadro elétrico"
        placeholder="Informe"
        value={data.panelName || ""}
        onChange={handleFieldChange("panelName")}
      />

      <FormControl fullWidth>
        <InputLabel id={`select-panel-type-${position}`}>Tipo</InputLabel>
        <Select
          labelId={`select-panel-type-${position}`}
          label="Tipo"
          value={data.panelTypeId || ""}
          onChange={handlePanelTypeChange}
        >
          {panelOptions?.map(({ id, name }) => (
            <MenuItem value={id} key={id}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Grid
        item
        container
        direction="row"
        justifyContent="space-between"
        gap={2}
      >
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id={`select-tension-${position}`}>
            Tensão nominal
          </InputLabel>
          <Select
            label="Tensão nominal"
            labelId={`select-tension-${position}`}
            value={data.tension || ""}
            onChange={handleFieldChange("tension")}
          >
            {TENSION_OPTIONS.map((tension) => (
              <MenuItem value={tension} key={tension}>
                {tension}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Corrente nominal (A)"
          placeholder="Informe"
          value={data.current || ""}
          onChange={handleFieldChange("current")}
          sx={{ flex: 1 }}
        />
      </Grid>

      <FormControl fullWidth>
        <InputLabel id={`select-conservation-${position}`}>
          Estado de conservação
        </InputLabel>
        <Select
          labelId={`select-conservation-${position}`}
          label="Estado de conservação"
          value={data.conservationStatus || ""}
          onChange={handleFieldChange("conservationStatus")}
        >
          {CONSERVATION_STATES.map((option) => (
            <MenuItem value={option} key={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Observações"
        placeholder="Descreva sua observação"
        value={data.obs || ""}
        onChange={handleFieldChange("obs")}
      />

      <PhotoUploadGrid
        photos={data.photos}
        onPhotosChange={handlePhotosChange}
        buttonText="Enviar foto"
      />

      <Typography variant="subtitle1" color="primary">
        Auditoria NR 10
      </Typography>

      <Grid container direction="column" gap={1.5}>
        {NR10_ITEMS.map((item) => (
          <Grid
            item
            container
            alignItems="center"
            justifyContent="space-between"
            key={item.key}
            gap={2}
          >
            <Typography sx={{ flex: 1 }}>{item.label}</Typography>
            <FormControl sx={{ minWidth: 90 }}>
              <Select
                value={data.nr10[item.key] ? "true" : "false"}
                onChange={handleNr10Change(item.key)}
              >
                {YES_NO_OPTIONS.map((option) => (
                  <MenuItem value={String(option.value)} key={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        ))}
      </Grid>

      {onDelete && <DeleteCTA onDeleteConfirmed={onDelete} />}
    </BlockStandard>
  );
};

export type Nr10Checklist = {
  panelIdentification: boolean;
  devicesIdentification: boolean;
  accidentalContactProtection: boolean;
  surgeProtectionDevice: boolean;
  groundingAndEquipotential: boolean;
  neutralEarthSeparation: boolean;
  cableSectionAdequacy: boolean;
  workLockout: boolean;
  warningPlates: boolean;
  electricalDiagrams: boolean;
  cableEntrySealing: boolean;
  cableOrganization: boolean;
  internalSpaceAdequacy: boolean;
};

export type ElectricalPanelDetailItem = {
  panelName: string;
  panelTypeId: string;
  tension: string;
  current: string;
  conservationStatus: MaintenanceState | "";
  obs: string;
  photos: File[];
  nr10: Nr10Checklist;
};

export const emptyElectricalPanel: ElectricalPanelDetailItem = {
  panelName: "",
  panelTypeId: "",
  tension: "",
  current: "",
  conservationStatus: "",
  obs: "",
  photos: [],
  nr10: {
    panelIdentification: false,
    devicesIdentification: false,
    accidentalContactProtection: false,
    surgeProtectionDevice: false,
    groundingAndEquipotential: false,
    neutralEarthSeparation: false,
    cableSectionAdequacy: false,
    workLockout: false,
    warningPlates: false,
    electricalDiagrams: false,
    cableEntrySealing: false,
    cableOrganization: false,
    internalSpaceAdequacy: false,
  },
};

export type ElectricalPanelDetailProps = {
  data: ElectricalPanelDetailItem;
  position: number;
  panelOptions: ElectricalPanelTypes[];
  onChange: (panel: ElectricalPanelDetailItem) => void;
  onDelete?: () => void;
};
